// @flow
import styles from "./EditSeries.scss";

import React, { useState, useEffect, useRef } from "react";

import get from "lodash.get";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import { type State as OrganisersState } from "../../../redux/modules/organisers.d";
import AsyncFilteredSelect from "../../AsyncFilteredSelect";
import isEqual from "lodash.isequal";
import SelectList from "../DefineEvents/SelectList/SelectList";

type Props = {
    translate: *,
    onClose: () => void,
    onSubmit: (event: *) => *,

    /** Disables the form */
    disabled: boolean,
    eventDetails: *,
    /** Organisers */
    loadOrganisers: *,
    organisers: OrganisersState,
    loadCategories: *,
    categories: *,
    id: string,
    /** Promoters */
    addPromoter: (value: *) => Promise<*>,
    addPromoterState: *,
    /** Series */
    editSeriesState: *,
    error?: *,
    refetchOrganisers: () => Promise<*>,
};

/**
 * Add dialog
 */
const EditSeries = ({
    onClose,
    translate,
    organisers,
    categories,
    id,
    addPromoterState,
    editSeriesState,
    disabled: disabledProp,
    eventDetails,
    loadCategories,
    loadOrganisers,
    onSubmit,
}: Props) => {
    const [seriesName, setSeriesName] = useState(null);
    const [internal, setInternal] = useState(null);
    const [promoterUuid, setPromoterUuid] = useState(null);
    const [promoterName, setPromoterName] = useState(null);
    const [optionCode, setOptionCode] = useState(null);
    const [category, setCategory] = useState("");

    const prevEventDetailsDataRef = useRef(null);

    const disabled = !!(
        disabledProp ||
        (editSeriesState && editSeriesState.loading)
    );

    const { error } = editSeriesState;

    useEffect(() => {
        loadCategories();
        loadOrganisers();
    }, []);

    /**
     * Sync state from eventDetails when it changes
     */
    useEffect(() => {
        const data = eventDetails && eventDetails.data;
        if (data && !isEqual(prevEventDetailsDataRef.current, data)) {
            prevEventDetailsDataRef.current = data;
            setSeriesName(data.name || "");
            setInternal(data.internal || "");
            setOptionCode(data.optionCode || "");
            setCategory(data.categoryUuid || "");
        }
    }, [eventDetails.data]);

    /**
     * Get errors
     */
    const getError = (key: string) => {
        let transformedKey = key;

        if (editSeriesState.fieldErrors) {
            return get(editSeriesState.fieldErrors, transformedKey);
        }

        if (addPromoterState.fieldErrors) {
            return get(addPromoterState.fieldErrors, transformedKey);
        }
    };
    /**
     * Submit handler
     */
    const handleSubmit = (event: *) => {
        event.preventDefault();
        const newEvent = {
            internal,
            seriesName,
            promoterUuid,
            optionCode,
            category,
        };
        onSubmit(newEvent);
    };

    return (
        <Dialog open>
            <form onSubmit={handleSubmit}>
                <DialogTitle disableTypography className={styles.title}>
                    <Typography variant="h2" color="inherit">
                        {translate("Edit event series")}
                    </Typography>
                    <Typography>
                        {translate(
                            "Edit all of the event at once in this series",
                        )}
                    </Typography>
                </DialogTitle>
                <DialogContent className={styles.content}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                className={styles.field}
                                id={`${id}-serie`}
                                label={translate("Event series")}
                                onChange={({ target: { value } }) =>
                                    setSeriesName(value)
                                }
                                value={seriesName || ""}
                                error={!!getError("name")}
                                helperText={getError("name")}
                                InputLabelProps={{
                                    style: { color: "white" },
                                }}
                                InputProps={{
                                    classes: {
                                        underline: styles.underline,
                                        disabled: styles.disabled,
                                        focused: styles.focused,
                                        error: styles.error,
                                    },
                                }}
                                disabled={disabled}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                className={styles.field}
                                id={`${id}-optionCode`}
                                label={translate("Option Code")}
                                onChange={({ target: { value } }) =>
                                    setOptionCode(value)
                                }
                                value={optionCode || ""}
                                error={!!getError("optionCode")}
                                helperText={getError("optionCode")}
                                disabled={disabled}
                                InputLabelProps={{
                                    style: {
                                        color: "white",
                                    },
                                }}
                                InputProps={{
                                    classes: {
                                        underline: styles.underline,
                                        disabled: styles.disabled,
                                        focused: styles.focused,
                                        error: styles.error,
                                    },
                                }}
                                fullWidth
                            />
                        </Grid>

                        <Grid
                            color="inherit"
                            style={{
                                opacity: "0.5",
                                display: "flex",
                                alignItems: "center",
                            }}
                            item
                            xs={6}
                            offset={6}
                        >
                            <SelectList
                                error={!!getError("categoryUuid")}
                                helperText={getError("categoryUuid")}
                                id="edit-series-chooseCategory"
                                onSelect={value => setCategory(value)}
                                venueCode="SEA-BLUE"
                                optionStatus={category}
                                color="light"
                                categories={categories}
                                type="CATEGORY"
                                label={translate("Category")}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                className={styles.field}
                                id={`${id}-internal`}
                                label={translate("Memo")}
                                onChange={({ target: { value } }) =>
                                    setInternal(value)
                                }
                                value={internal || ""}
                                error={!!getError("internal")}
                                helperText={getError("internal")}
                                disabled={disabled}
                                InputLabelProps={{
                                    style: {
                                        color: "white",
                                    },
                                }}
                                InputProps={{
                                    classes: {
                                        underline: styles.underline,
                                        disabled: styles.disabled,
                                        focused: styles.focused,
                                        error: styles.error,
                                    },
                                }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <AsyncFilteredSelect
                                id={`${id}-promoter`}
                                disabled={disabled}
                                variant="white"
                                error={
                                    !!(
                                        addPromoterState.error ||
                                        (addPromoterState.fieldErrors &&
                                            addPromoterState.fieldErrors
                                                .name) ||
                                        getError("promoterUuid")
                                    )
                                }
                                helperText={
                                    (!!addPromoterState.error &&
                                        addPromoterState.error) ||
                                    (addPromoterState.fieldErrors &&
                                        addPromoterState.fieldErrors.name) ||
                                    getError("promoterUuid")
                                }
                                label={translate("Organiser")}
                                asyncState={organisers}
                                onSelect={promoter => {
                                    setPromoterUuid(promoter.uuid || null);
                                    setPromoterName(
                                        promoter.uuid ? null : promoter.name,
                                    );
                                }}
                                itemToString={item => (item && item.name) || ""}
                                itemToLabel={item =>
                                    (item && (item.label || item.name)) || ""
                                }
                                filter={(value, items) =>
                                    items
                                        .filter(item =>
                                            item.name
                                                .toLowerCase()
                                                .includes(value.toLowerCase()),
                                        )
                                        .splice(0, 5)
                                }
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogContent>
                    <Grid container spacing={3}>
                        {
                            <Grid item xs={12}>
                                <Typography
                                    style={{
                                        fontWeight: "bold",
                                        marginTop: "1em",
                                    }}
                                >
                                    {translate("Caution")}:
                                </Typography>
                                <Typography
                                    style={{
                                        opacity: "0.75",
                                    }}
                                >
                                    {translate("Warning Edit Series")}.
                                </Typography>
                                {promoterName ||
                                    (promoterUuid && (
                                        <Typography
                                            style={{
                                                fontWeight: "bold",
                                                marginTop: "0.5em",
                                            }}
                                        >
                                            {translate("Warning Save Changes")}
                                        </Typography>
                                    ))}
                            </Grid>
                        }
                        {error && (
                            <Typography color="error">{error}</Typography>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        id={`${id}-addshow-cancel`}
                        onClick={onClose}
                        disabled={disabled}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        id={`${id}-submit`}
                        type="submit"
                        disabled={disabled}
                        color="primary"
                    >
                        {translate("Save changes")}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default EditSeries;
